import { useEffect, useRef, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { motion, useInView } from "framer-motion";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&family=Jost:wght@300;400;500&display=swap');
`;

const STATS = [
  {
    value: 15,
    suffix: "",
    label: "Years of Experience",
    note: "Capturing the world since 2009",
  },
  {
    value: 6000,
    suffix: "+",
    label: "Prints Sold",
    note: "Shipped to collectors worldwide",
  },
  {
    value: 98,
    suffix: "%",
    label: "5-Star Reviews",
    note: "Trusted by thousands of customers",
  },
];

/* ── Keyframes ── */
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

/* ── Styled Components ── */
const Section = styled.section`
  background: #0a0804;
  padding: 7rem 5vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4rem;
`;

const Eyebrow = styled(motion.p)`
  font-family: "Jost", sans-serif;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #c9a96e;
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
  max-width: 900px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Divider = styled.div`
  width: 1px;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(201, 169, 110, 0.25),
    transparent
  );
  align-self: stretch;

  @media (max-width: 640px) {
    display: none;
  }
`;

const StatCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem 2rem;
  gap: 0.5rem;

  @media (max-width: 640px) {
    padding: 2.5rem 1rem;
    border-bottom: 1px solid rgba(201, 169, 110, 0.12);
    &:last-child {
      border-bottom: none;
    }
  }
`;

const StatNumber = styled.span`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: clamp(3.5rem, 6vw, 5rem);
  line-height: 1;
  letter-spacing: -0.02em;
  background: linear-gradient(
    90deg,
    #c9a96e 0%,
    #f0d9a8 40%,
    #c9a96e 60%,
    #a07840 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 5s linear infinite;
`;

const StatLabel = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #f5ede0;
`;

const StatNote = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 0.72rem;
  font-weight: 300;
  color: rgba(245, 237, 224, 0.38);
  letter-spacing: 0.04em;
`;

/* ── Animated counter hook ── */
function useCounter(target, inView, duration = 1800) {
  const [count, setCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, target, duration]);

  return count;
}

/* ── Single stat ── */
function Stat({ value, suffix, label, note, index, inView }) {
  const count = useCounter(value, inView, 1600 + index * 200);

  return (
    <StatCard
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.15, ease: "easeOut" }}
    >
      <StatNumber>
        {count.toLocaleString()}
        {suffix}
      </StatNumber>
      <StatLabel>{label}</StatLabel>
      <StatNote>{note}</StatNote>
    </StatCard>
  );
}

/* ── Section ── */
export default function Stats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <>
      <GlobalStyle />
      <Section ref={ref}>
        <Eyebrow
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          Bess Hamiti — By the Numbers
        </Eyebrow>

        <Grid>
          {STATS.map((stat, i) => (
            <>
              <Stat key={stat.label} {...stat} index={i} inView={inView} />
              {i < STATS.length - 1 && <Divider key={`div-${i}`} />}
            </>
          ))}
        </Grid>
      </Section>
    </>
  );
}
